/*
  Analítica de comportamiento por componente (#310).

  Empuja al dataLayer dos eventos genéricos con esquema uniforme:
    - ui_click       {surface, component, action, target, position}
    - component_view {surface, component}  (una vez por componente por render)

  "Por render": en colección y búsqueda la paginación, el orden y el tamaño de
  página son AJAX (theme.js reemplaza .collection__dynamic-part y dispara
  theme:loading:end); cada re-render de toolbar, grid y paginación cuenta como
  una exposición nueva (#334).

  Solo emite para elementos marcados con data-analytics-component en el markup;
  nada de selectores CSS en GTM. Los pushes son inertes hasta que el contenedor
  tenga tags que escuchen estos nombres de evento (aditivo: no toca los eventos
  de custom-datalayer ni los de la app Google & YouTube).
*/
(function () {
  'use strict';

  var surface = (document.currentScript && document.currentScript.dataset.surface) || 'other';
  // En orden de prioridad: el marcador explícito gana; luego la card de producto
  // aunque esté envuelta en un grid__cell.
  var ITEM_SELECTORS = ['[data-analytics-item]', '.product-item', '.grid__cell', '.quad-block', 'li'];

  function push(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function componentOf(el) {
    return el.closest('[data-analytics-component]');
  }

  // Posición 1-based del ítem clickeado dentro del componente (orden del DOM),
  // contada entre los ítems del mismo tipo que el clickeado.
  function positionOf(target, component) {
    for (var s = 0; s < ITEM_SELECTORS.length; s++) {
      var item = target.closest(ITEM_SELECTORS[s]);
      if (!item || !component.contains(item)) continue;
      var siblings = component.querySelectorAll(ITEM_SELECTORS[s]);
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] === item) return i + 1;
      }
    }
    return undefined;
  }

  function targetOf(el, component) {
    var explicit = el.closest('[data-analytics-target]');
    if (explicit && component.contains(explicit)) return explicit.dataset.analyticsTarget;
    var link = el.closest('a[href]');
    if (link && component.contains(link)) {
      try {
        var url = new URL(link.href, location.origin);
        // Interno: solo la ruta. Externo: host + ruta, para que "/Y0flE" no
        // parezca una ruta propia rota cuando en realidad es zcform.com/Y0flE.
        return url.host === location.host ? url.pathname : url.host + url.pathname;
      } catch (e) { /* href inválido: cae al fallback */ }
    }
    var button = el.closest('button, [type="submit"]');
    if (button && component.contains(button)) {
      return (button.textContent || '').trim().slice(0, 40) || 'button';
    }
    return undefined;
  }

  document.addEventListener('click', function (event) {
    // Solo clics del usuario: descarta los sintéticos de element.click() (un botón
    // que reenvía su clic a otro). El clic que el navegador genera sobre el control
    // al tocar un <label> sí es isTrusted y lo resuelve la regla de abajo.
    if (!event.isTrusted) return;
    if (!(event.target instanceof Element)) return;
    // Un tap en un <label> con control asociado dispara dos clicks: el del label
    // y uno sintético sobre el control (checkbox de filtros). Se cuenta solo el
    // del control; el click directo sobre el control no genera el segundo.
    var label = event.target.closest('label');
    if (label && label.control && !label.control.contains(event.target)) return;

    var component = componentOf(event.target);
    if (!component) return;

    var actionEl = event.target.closest('[data-analytics-action]');
    // target/position siempre presentes (null si no aplican): el modelo de GTM
    // persiste valores entre pushes y un key ausente heredaría el del push anterior.
    push({
      event: 'ui_click',
      surface: surface,
      component: component.dataset.analyticsComponent,
      action: (actionEl && component.contains(actionEl)) ? actionEl.dataset.analyticsAction : 'click',
      target: targetOf(event.target, component) || null,
      position: positionOf(event.target, component) || null
    });
  }, { capture: true, passive: true });
  // capture: los handlers del tema hacen stopPropagation en algunos controles
  // (p. ej. abrir el drawer de filtros) y en burbuja el click nunca llega a document.

  var observer = null;
  var observed = new WeakSet(); // ya está bajo observación
  var seen = new WeakSet();     // ya emitió su vista

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      if (seen.has(entry.target)) return;
      // Visible: 30% del componente, o media pantalla para componentes muy altos.
      // ratio > 0 y no isIntersecting: un elemento pegado al borde del viewport
      // (el drawer de filtros cerrado, translateX(100%)) "intersecta" con ratio 0
      // y un rectángulo de ancho 0 pero altura completa.
      // Los umbrales bajos existen para que esta regla se evalúe a tiempo en
      // componentes de varias pantallas (un grid de 24 productos en móvil mide
      // ~5 pantallas y nunca llega al 30%).
      var tallEnough = entry.intersectionRect.height >= window.innerHeight * 0.5;
      if (entry.intersectionRatio >= 0.3 || (entry.intersectionRatio > 0 && tallEnough)) {
        seen.add(entry.target);
        observer.unobserve(entry.target);
        push({
          event: 'component_view',
          surface: surface,
          component: entry.target.dataset.analyticsComponent,
          target: entry.target.dataset.analyticsTarget || null
        });
      }
    });
  }

  // Observa los componentes que aún no lo están. Se llama al cargar y tras cada
  // re-render AJAX (theme:loading:end), porque los nodos nuevos no heredan la
  // observación de los que reemplazan.
  function observeNew() {
    if (!('IntersectionObserver' in window)) return;
    var tracked = document.querySelectorAll('[data-analytics-component]');
    if (!tracked.length) return;
    if (!observer) {
      observer = new IntersectionObserver(onIntersect, { threshold: [0, 0.01, 0.02, 0.05, 0.1, 0.2, 0.3] });
    }
    tracked.forEach(function (el) {
      if (observed.has(el)) return;
      observed.add(el);
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeNew);
  } else {
    observeNew();
  }
  document.addEventListener('theme:loading:end', observeNew);
})();
